import { useState } from "react"
import { useParams } from "react-router-dom"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { 
  UserPlus, 
  Mail, 
  Lock, 
  User, 
  Phone, 
  CheckCircle, 
  AlertCircle,
  ArrowRight 
} from "lucide-react"
import { acceptInvitation } from "@/lib/api/team"

export default function AcceptInvitationPage() {
  const { token } = useParams<{ token: string }>()

  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    password: "",
    confirmPassword: "",
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Alert variant="destructive">
          <AlertDescription>ลิงก์คำเชิญไม่ถูกต้อง</AlertDescription>
        </Alert>
      </div>
    )
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validation
    if (!formData.fullName) {
      setError("กรุณาระบุชื่อ-นามสกุล")
      return
    }

    if (formData.fullName.length < 2) {
      setError("ชื่อต้องมีอย่างน้อย 2 ตัวอักษร")
      return
    }

    if (!formData.password) {
      setError("กรุณาระบุรหัสผ่าน")
      return
    }

    if (formData.password.length < 8) {
      setError("รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร")
      return
    }

    if (formData.password !== formData.confirmPassword) {
      setError("รหัสผ่านไม่ตรงกัน")
      return
    }

    if (formData.phone && !/^0[6-9]\d{8}$/.test(formData.phone)) {
      setError("รูปแบบเบอร์โทรศัพท์ไม่ถูกต้อง")
      return
    }

    try {
      setLoading(true)
      
      await acceptInvitation(token, {
        fullName: formData.fullName,
        password: formData.password,
        phone: formData.phone || undefined,
      })

      setSuccess(true)
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        window.location.href = '/auth/login'
      }, 3000)
    } catch (err: any) {
      console.error('Failed to accept invitation:', err)
      const errorMessage = err.response?.data?.error || 'ไม่สามารถยอมรับคำเชิญได้'
      
      if (errorMessage.includes('หมดอายุ')) {
        setError('คำเชิญหมดอายุแล้ว กรุณาติดต่อผู้ดูแลระบบเพื่อขอคำเชิญใหม่')
      } else if (errorMessage.includes('ใช้งานแล้ว')) {
        setError('คำเชิญนี้ถูกใช้งานแล้ว')
      } else if (errorMessage.includes('ไม่พบ')) {
        setError('ไม่พบคำเชิญนี้ กรุณาตรวจสอบลิงก์อีกครั้ง')
      } else {
        setError(errorMessage)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-blue-600 text-white mb-4">
            <UserPlus className="h-8 w-8" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            ยินดีต้อนรับเข้าสู่ทีม
          </h1>
          <p className="text-gray-600">
            กรุณากรอกข้อมูลเพื่อสร้างบัญชีของคุณ
          </p>
        </div>

        {/* Success State */}
        {success ? (
          <Card className="border-green-200 bg-green-50">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 text-green-600 mb-4">
                  <CheckCircle className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-semibold text-green-900 mb-2">
                  สร้างบัญชีสำเร็จ!
                </h3>
                <p className="text-green-700 mb-4">
                  ยินดีต้อนรับเข้าสู่ทีม คุณสามารถเข้าสู่ระบบได้แล้ว
                </p>
                <Button
                  onClick={() => window.location.href = '/auth/login'}
                  className="bg-green-600 hover:bg-green-700"
                >
                  เข้าสู่ระบบ
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>สร้างบัญชีของคุณ</CardTitle>
              <CardDescription>
                กรอกข้อมูลด้านล่างเพื่อยอมรับคำเชิญและเข้าร่วมทีม
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Full Name */}
                <div className="space-y-2">
                  <Label htmlFor="fullName">
                    ชื่อ-นามสกุล <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="fullName"
                      name="fullName"
                      type="text"
                      placeholder="กรอกชื่อ-นามสกุล"
                      value={formData.fullName}
                      onChange={handleChange}
                      disabled={loading}
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Phone (Optional) */}
                <div className="space-y-2">
                  <Label htmlFor="phone">
                    เบอร์โทรศัพท์ <span className="text-gray-400">(ไม่บังคับ)</span>
                  </Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="0812345678"
                      value={formData.phone}
                      onChange={handleChange}
                      disabled={loading}
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Password */}
                <div className="space-y-2">
                  <Label htmlFor="password">
                    รหัสผ่าน <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      placeholder="อย่างน้อย 8 ตัวอักษร"
                      value={formData.password}
                      onChange={handleChange}
                      disabled={loading}
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Confirm Password */}
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">
                    ยืนยันรหัสผ่าน <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      placeholder="กรอกรหัสผ่านอีกครั้ง"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      disabled={loading}
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Error Alert */}
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {/* Info Alert */}
                <Alert className="bg-blue-50 border-blue-200">
                  <Mail className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-800 text-sm">
                    คุณจะได้รับอีเมลยืนยันหลังจากสร้างบัญชีเรียบร้อยแล้ว
                  </AlertDescription>
                </Alert>

                {/* Submit Button */}
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      กำลังสร้างบัญชี...
                    </>
                  ) : (
                    <>
                      <UserPlus className="mr-2 h-4 w-4" />
                      สร้างบัญชีและเข้าร่วมทีม
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-6">
          มีบัญชีอยู่แล้ว?{' '}
          <a href="/auth/login" className="text-purple-600 hover:underline">
            เข้าสู่ระบบ
          </a>
        </p>
      </div>
    </div>
  )
}
